import { Controller } from '@hotwired/stimulus'
import DynamicSelect from '../../modules/dynamic_select'

export default class extends Controller {
  static targets = [
    'type',
    'email',
    'mobile',
    'userId',
    'companyRoleId',
    'auditorRoleId',
    'authorityRoleId',
    'projectFieldId',
    'facilityFieldId'
  ]

  static values = {
    nameOption: String,
    valueOption: String,
    actionName: String
  }

  declare readonly typeTarget: HTMLSelectElement
  declare readonly userIdTarget: HTMLSelectElement
  declare readonly companyRoleIdTarget: HTMLSelectElement
  declare readonly auditorRoleIdTarget: HTMLSelectElement
  declare readonly authorityRoleIdTarget: HTMLSelectElement
  declare readonly projectFieldIdTarget: HTMLSelectElement
  declare readonly facilityFieldIdTarget: HTMLSelectElement
  declare readonly emailTarget: HTMLInputElement
  declare readonly mobileTarget: HTMLInputElement

  declare readonly hasUserIdTarget: boolean
  declare readonly hasAuditorRoleIdTarget: boolean
  declare readonly hasProjectFieldIdTarget: boolean
  declare readonly hasFacilityFieldIdTarget: boolean
  declare readonly hasMobileTarget: boolean
  declare readonly actionNameValue: string

  recipientTypesSelect: DynamicSelect

  connect (): void {
    this.recipientTypesSelect = new DynamicSelect(this.typeTarget)
    setTimeout(() => {
      this.dispatch('connected')
      this.changeType()
    })
  }

  update ({ detail: { enable, types } }: CustomEvent): void {
    this.recipientTypesSelect.reset(types)
    this.toggle(this.typeTarget, enable)
    this.changeType()
  }

  changeType (): void {
    this.allInputs.forEach(input => this.toggle(input, false))

    const input = this.recipientInput()

    if (input != null) {
      this.toggle(input, true)
    }
  }

  private toggle (input: HTMLInputElement | HTMLSelectElement, show: boolean): void {
    input.disabled = !show || input.getAttribute('data-readonly') === 'true'

    this.toggleHiddenClass(input, show)
  }

  private toggleHiddenClass (el: Element, show: boolean): void {
    const shouldShowElement = (): boolean => {
      if (this.actionNameValue === 'show') {
        if (show) return true

        if (el instanceof HTMLSelectElement) {
          return Array.from(el.options).some(option => option.hasAttribute('selected'))
        }

        return el instanceof HTMLInputElement && el.value !== ''
      }

      return show
    }

    if (shouldShowElement()) {
      el.classList.remove('hidden')
    } else {
      el.classList.add('hidden')
    }
  }

  private recipientInput (): HTMLInputElement | HTMLSelectElement | null {
    if (this.typeTarget.disabled) {
      return null
    }

    switch (this.typeTarget.value) {
      case 'email':
        return this.emailTarget
      case 'sms':
        return this.mobileTarget
      case 'company_role':
        return this.companyRoleIdTarget
      case 'auditor_role':
        return this.auditorRoleIdTarget
      case 'authority_role':
        return this.authorityRoleIdTarget
      case 'project_email_field':
        return this.projectFieldIdTarget
      case 'facility_email_field':
        return this.facilityFieldIdTarget
      case 'user':
        return this.userIdTarget
    }

    return null
  }

  private get allInputs (): Array<HTMLInputElement | HTMLSelectElement> {
    const inputs = [
      this.emailTarget,
      this.userIdTarget,
      this.companyRoleIdTarget,
      this.authorityRoleIdTarget
    ]

    if (this.hasMobileTarget) {
      inputs.push(this.mobileTarget)
    }

    if (this.hasAuditorRoleIdTarget) {
      inputs.push(this.auditorRoleIdTarget)
    }

    if (this.hasProjectFieldIdTarget) {
      inputs.push(this.projectFieldIdTarget)
    }

    if (this.hasFacilityFieldIdTarget) {
      inputs.push(this.facilityFieldIdTarget)
    }

    return inputs
  }
}
